import { api } from './index'

/**
 * The `intro` sample dataset (VRG-512).
 *
 * Company scoping is implicit — `createApiCall` injects `X-Vera-Company` from the
 * selected company, and the API reads the company from the auth guard, never from a
 * path or body. There is deliberately no `companyId` parameter here: adding one would
 * suggest the endpoint can target another tenant, which it cannot.
 *
 * Uses the `api.*` wrapper rather than the local status-guard pattern in
 * `tourProgressApi`. That file's `ok()` helper exists because `createApiCall` once
 * RESOLVED with error responses; `services/index.ts:241-272` now documents that it
 * REJECTS on every >= 400, so the guard is vestigial and its comment is stale. New
 * callers should let the rejection propagate to react-query.
 */

export interface IntroDataStatus {
  /** The tenant currently holds sample rows. */
  present: boolean
  /**
   * The tenant is ELIGIBLE for sample rows (it has no real products yet).
   *
   * Distinct from `!present`: a tenant with real data is neither holding samples nor
   * able to receive them, and an affordance keyed on `!present` alone offers an action
   * that silently no-ops.
   */
  canSeed: boolean
  /**
   * Has a REAL (non-sample) certificate shown up for this tenant yet (VRG-512)?
   *
   * A THIRD, independent question from `present`/`canSeed` — not "are there
   * samples" but "has real data arrived". The server's analytics aggregates apply
   * the identical predicate: the moment this flips `true`, every chart and KPI on
   * the Analytics page has ALREADY stopped counting sample rows, whether or not
   * the sample set has been cleared (`present` can still be `true` here — clearing
   * is a separate, user-initiated action, not automatic). `IntroDataBanner` reads
   * this to announce that transition instead of letting a number drop unexplained.
   */
  hasRealData: boolean
  /**
   * Has this tenant cleared its intro sample set and NOT re-seeded since (VRG-512)?
   *
   * A FOURTH question the other three cannot answer between them. `{present: false,
   * canSeed: true}` is the identical response for a brand-new company that never had
   * samples and for one that had them and deliberately cleared them — and Home treats
   * those differently, giving the first the full offer and the second a collapsed
   * accordion, because re-offering prominently second-guesses a decision the user just
   * made.
   *
   * Server-recorded (`companies.intro_cleared_at`, written inside the clear's own
   * transaction), NOT a client flag: clearing is tenant-level, so a per-device or
   * per-browser record would show one admin the collapsed card on their desktop and the
   * full offer on their laptop, and would show a second admin at the same brand an
   * offer they never had the chance to decline.
   *
   * ⚠️ NOT "ever" — the stamp is CLEARED AGAIN by the next seed
   * (`materialize-intro-set.use-case.ts` step 13), in that seed's own transaction. A
   * tenant that cleared and then re-seeded is `{present: true, hasCleared: false}`, so
   * the accordion returns only after they clear once more. Read as "ever", this field
   * would outlive the decision it records: it means "is done with samples", and a fresh
   * seed is that decision being reversed by the same explicit user action.
   *
   * Only consulted while `present` is false — with samples present the card shows its
   * clear arm regardless — so the reset is not what makes the common paths correct. It
   * is what keeps the field honest on the path where samples leave WITHOUT going through
   * the clear button, which a sticky stamp would answer with a collapsed accordion over a
   * decision the user never made.
   */
  hasCleared: boolean
  /**
   * ISO timestamp of the OLDEST sample row this tenant holds, or `null` when it holds
   * none (VRG-512 v10).
   *
   * A FIFTH question, and the only one that is not a boolean — because it is not asking
   * whether samples exist but WHEN they claim to have happened. The sample set fabricates
   * ~11 months of history into a tenant that may have signed up minutes ago.
   *
   * `RoiHero` is the consumer. That card measures "all time" and floors the window at
   * `companies.created_at`, which is the earliest instant a REAL brand can have data and
   * therefore excludes almost the whole sample history: measured on a seeded tenant it
   * reported 2 transfers and $19,000 against 22 and $125,000 actually present, with
   * `median_days_to_first_scan` empty. The card floors at `min(created_at, this)` instead.
   *
   * ⚠️ NOT derivable on the client. The ROI response is computed OVER the window, so
   * reading it back to choose the window is circular — and the server deliberately does
   * NOT fix this by backdating `companies.created_at`, which is real unfenced data a
   * clear could not restore.
   *
   * `null` on a tenant with no samples, which makes the floor a no-op: a real brand's
   * window must not silently widen.
   */
  earliestEventAt: string | null
}

export interface MaterializeIntroSetResult {
  created: boolean
  reason?: 'already-present' | 'tenant-has-real-data'
  productIds: string[]
  certificateIds: string[]
  version: number
}

export interface ClearIntroSetResult {
  scans: number
  /** VRG-512 v2 — transfers between sample owners, deleted before the certificates they reference. */
  transfers: number
  certificates: number
  products: number
  /** VRG-512 v2 — physical sample tags (one per sample product, plus a couple of spares). */
  tags: number
  owners: number
}

/**
 * The sample-set sections a client may ask to clear ON THEIR OWN (VRG-512).
 *
 * ⚠️ ONLY the sections the server marks clearable in `intro-sections.ts`. This is
 * deliberately NOT the full list of surfaces that show samples: `owners`, `products` and
 * `orders` are all missing because scoping them is not currently honest — clearing sample
 * people would CASCADE-delete their certificates, clearing sample products throws on a
 * RESTRICT — and the server 400s on each. Keeping them out of the type means a per-surface
 * prompt for one of them cannot be written by accident; it has to go and read why first.
 */
export type ClearableIntroSection = 'certificates'

/**
 * What a section clear removed. Scoped counts, never the whole set's.
 *
 * `scans` and `transfers` are not a side effect to be surprised by — they are facts about
 * the certificates being removed and cannot outlive them. The prompt's copy says so.
 */
export interface ClearIntroSectionResult {
  section: ClearableIntroSection
  certificates: number
  scans: number
  transfers: number
}

export interface DeleteIntroCertificateResult {
  /**
   * `false` for absent, other-company, AND real certificates alike — the API's own
   * doc on `removeCertificate` says this is deliberate: the response can't be used to
   * probe which ids are real, and the endpoint's own WHERE clause (`example_kind =
   * 'intro'`) is what actually prevents a real certificate from being touched, not this
   * flag. Never surface a "not found" error off a bare `false` — it isn't one.
   */
  deleted: boolean
  scansDeleted: number
}

/** Which affordance asked for the set. Telemetry only — never gates the write. */
export type IntroTrigger = 'home-cta' | 'tour-start'

export const introDataApi = {
  /** Does the selected company currently hold intro sample data? */
  async status(): Promise<IntroDataStatus> {
    const response = await api.get('/intro-data')
    return response.data as IntroDataStatus
  },

  /**
   * Add the intro set. Idempotent — a second call returns the existing set with
   * `created: false`, so a caller that fires on every tour launch is safe.
   */
  async create(trigger: IntroTrigger): Promise<MaterializeIntroSetResult> {
    const response = await api.post('/intro-data', { trigger })
    return response.data as MaterializeIntroSetResult
  },

  /** Remove the whole set. Idempotent — reports zeroes rather than failing. */
  async clear(): Promise<ClearIntroSetResult> {
    const response = await api.delete('/intro-data')
    return response.data as ClearIntroSetResult
  },

  /**
   * Remove ONE surface's samples, leaving the rest of the set in place (VRG-512).
   *
   * ⚠️ The server REFUSES a section it cannot scope honestly (400) rather than widening
   * to a full clear — `intro-sections.ts` records which and why. So a caller must never
   * treat a failure here as "fall back to clearing everything": that is precisely the
   * substitution the refusal exists to prevent.
   */
  async clearSection(section: ClearableIntroSection): Promise<ClearIntroSectionResult> {
    const response = await api.delete(`/intro-data/sections/${section}`)
    return response.data as ClearIntroSectionResult
  },

  /**
   * Delete ONE sample certificate (AC4 — per-row "Remove sample"). The server's own
   * delete predicate carries `example_kind = 'intro'`, so this cannot express the
   * removal of a real certificate regardless of what id is passed.
   */
  async deleteCertificate(certificateId: string): Promise<DeleteIntroCertificateResult> {
    const response = await api.delete(`/intro-data/certificates/${certificateId}`)
    return response.data as DeleteIntroCertificateResult
  },
}
